import {emptyArray} from '@packages/utilities/fp/utils';
import {
  type Type,
  Expressions,
  matchExpression,
  Variable,
} from '@packages/systems/wfdl';
import {append} from '@packages/utilities/array';
import {
  type Expression,
  EVariable,
  ECall,
  ESelect,
  TGeneric,
  functionOf,
  constructTypeApplication,
  TTypeReference,
} from '@packages/systems/wfdl-ast';
import {identifier} from '@packages/systems/wfdl-Qualified';

// Re-export some handy utilities from the wfdl package
export const call = Expressions._call;
export const func = Expressions._func;
export const tyapp = constructTypeApplication;
export {a, b, c, r} from '@packages/systems/wfdl-ast';

/**
 * Type reference to 'error'
 */
export const error: Type = TTypeReference('error');

/**
 * Short alias for `qualifiedVariable`
 */
export const _$ = Expressions.qualifiedVariable;

/**
 * Shorthand for constructing an unqualified variable
 */
export const $ = (name: string): Expression =>
  EVariable(Variable.unqualified(identifier(name)));

/**
 * Create a JS function that builds a call of a unary WFDL function.
 */
export const call1 =
  (fun: Expression): ((arg: Expression) => Expression) =>
  (arg: Expression): Expression =>
    ECall({fun, arg});

/**
 * Create a JS function that builds a call of a binary WFDL function.
 */
export const call2 =
  (fun: Expression): ((arg1: Expression, arg2: Expression) => Expression) =>
  (arg1: Expression, arg2: Expression): Expression =>
    call(fun, [arg1, arg2]);

/**
 * Create a JS function that builds a call of a ternary WFDL function.
 */
export const call3 =
  (
    fun: Expression
  ): ((arg1: Expression, arg2: Expression, arg3: Expression) => Expression) =>
  (arg1: Expression, arg2: Expression, arg3: Expression): Expression =>
    call(fun, [arg1, arg2, arg3]);

const sel = (from: Expression, prop: string) => ESelect({from, prop});
/**
 * Select a property in a nested record
 */
export const at: (rec: Expression, path: Array<string>) => Expression = (
  rec: Expression,
  path: Array<string>
) => path.reduce(sel, rec);

const buildGeneric = (body: Type, arg: string) => TGeneric({arg, body});

/**
 * Create multi-parameter generic types
 *
 * The name `forall` comes from _universal quantification_ in mathematical
 * logic and is used as a keyword in functional languages such as PureScript
 * on which WFDL is based.
 *
 * Example:
 *
 * ```
 * // the following call:
 * forall(['a', 'b'], tyfun([a, b], TRecord({first: a, second: b})))
 *
 * // creates a WFDL type equivalent to the following Flow signature
 * <a, b>(a) => (b) => {first: a, second: b}
 * ```
 *
 * [Universal quantification]: https://en.wikipedia.org/wiki/Universal_quantification
 * [Forall keyword in PureScript]: https://jordanmartinez.github.io/purescript-jordans-reference-site/content/11-Syntax/01-Basic-Syntax/src/04-Various-Keywords/01-Keyword--Forall-ps.html
 */
export const forall = (typeParameters: Array<string>, body: Type): Type =>
  typeParameters.reduceRight(buildGeneric, body);

const buildFun = (ret: Type, arg: Type) => functionOf(arg, ret);

/**
 * Create Function types with multiple arguments
 */
export const tyfun = (args: Array<Type>, ret: Type): Type =>
  args.reduceRight(buildFun, ret);

export const toSelectPath = (
  expression: Expression
): {
  from: Expression;
  path: Array<string>;
} =>
  matchExpression<{
    from: Expression;
    path: Array<string>;
  }>({
    Select: ({from, prop}) => {
      const inner = toSelectPath(from);
      return {from: inner.from, path: append(inner.path, prop)};
    },
    _: (expr) => ({from: expr, path: emptyArray}),
  })(expression);
